import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';

@Component({
  selector: 'andor-context-menu',
  standalone: true,
  templateUrl: './context-menu.html',
  styleUrl: './context-menu.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContextMenu {
  mode = input<'styled' | 'transparent'>('styled');
  xPosition = input<'before' | 'after'>('after');
  yPosition = input<'above' | 'below'>('below');
  overlapTrigger = input(true);
  // When true, the panel is sized to the trigger's width. Used by select-style
  // dropdowns that should line up with their field; default off so the usual
  // content-sized menus are unaffected.
  matchTriggerWidth = input(false);

  opened = output<void>();
  closed = output<void>();

  isOpen = signal(false);
  // Projected content is gated behind this signal so closed menus don't pay
  // the cost of rendering their panel subtree. Flips to true on first open
  // and stays true — opened-once panels stay in the DOM so subsequent opens
  // are instant and UI state (form inputs, scroll position) is preserved.
  hasBeenOpened = signal(false);

  @ViewChild('panel') panelRef!: ElementRef<HTMLElement>;

  panelStyle: Record<string, string> = {};

  // Registry of currently open menus. Used so that a press inside a child
  // popover (e.g. a nested context-menu opened from within this one) is not
  // treated as an outside click.
  private static _openMenus: { panel: HTMLElement; trigger: HTMLElement | null }[] = [];

  private _triggerEl: HTMLElement | null = null;
  private _originalParent: HTMLElement | null = null;
  private _rafId: number | null = null;
  private _xOverride: 'before' | 'after' | null = null;
  private _yOverride: 'above' | 'below' | null = null;
  private _cdr = inject(ChangeDetectorRef);
  private _destroyRef = inject(DestroyRef);

  constructor() {
    this._destroyRef.onDestroy(() => {
      // If the host is destroyed while the menu is still open (e.g. CDK
      // virtual-scroll recycled the row that owns this menu while its panel
      // was portaled to <body>), `close()` never ran — splice the stale
      // entry so the static registry doesn't retain a detached panel/trigger.
      const panelEl = this.panelRef?.nativeElement;
      if (panelEl) {
        const idx = ContextMenu._openMenus.findIndex((m) => m.panel === panelEl);
        if (idx !== -1) ContextMenu._openMenus.splice(idx, 1);
      }
      this._removeListeners();
      this._demoteFromTopLayer();
      this._returnPanelToOriginalParent();
    });
  }

  /**
   * Add the panel to the browser top layer via the Popover API so it stacks
   * above CDK overlays (which use the same mechanism). `manual` mode disables
   * the browser's light-dismiss — this component owns open/close. Feature-
   * detected: on browsers without Popover support this is a no-op and the panel
   * falls back to its `z-index` (`--z-index-popover`).
   */
  private _promoteToTopLayer() {
    const panelEl = this.panelRef?.nativeElement as HTMLElement & {
      showPopover?: () => void;
    };
    if (!panelEl || typeof panelEl.showPopover !== 'function') return;
    if (panelEl.getAttribute('popover') !== 'manual') {
      panelEl.setAttribute('popover', 'manual');
    }
    try {
      if (!panelEl.matches(':popover-open')) panelEl.showPopover();
    } catch {
      // Not connected, or `:popover-open` unsupported — ignore and fall back.
    }
  }

  /** Remove the panel from the top layer (idempotent). */
  private _demoteFromTopLayer() {
    const panelEl = this.panelRef?.nativeElement as HTMLElement & {
      hidePopover?: () => void;
    };
    if (!panelEl || typeof panelEl.hidePopover !== 'function') return;
    try {
      if (panelEl.matches(':popover-open')) panelEl.hidePopover();
    } catch {
      // ignore
    }
  }

  toggle(triggerEl: HTMLElement) {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open(triggerEl);
    }
  }

  // `options` lets a caller override the declared inputs at open-time without
  // a separate CD pass. Setting a signal-backed input from the parent and
  // calling `open()` in the same tick would otherwise read the stale value
  // because signal-input updates propagate on the next change-detection cycle.
  open(
    triggerEl: HTMLElement,
    options?: { xPosition?: 'before' | 'after'; yPosition?: 'above' | 'below' }
  ) {
    const firstOpen = !this.hasBeenOpened();
    if (firstOpen) this.hasBeenOpened.set(true);

    this._triggerEl = triggerEl;
    this._xOverride = options?.xPosition ?? null;
    this._yOverride = options?.yPosition ?? null;
    this._movePanelToBody();
    // Promote into the browser top layer BEFORE measuring/positioning so the
    // panel has layout (a `popover` element is `display:none` until shown) and
    // so it renders above CDK overlays — Material dialogs/menus are shown by CDK
    // via the Popover API (`popover="manual"`), and the top layer beats any
    // z-index.
    this._promoteToTopLayer();
    // On the first open, force a synchronous CD pass so the lazy
    // `<ng-content />` is projected before `_computePosition()` reads
    // `panelEl.offsetWidth / offsetHeight` for the viewport-flip math.
    // Without this, the very first open would measure an empty panel.
    if (firstOpen) this._cdr.detectChanges();
    this.panelStyle = this._computePosition();
    this.isOpen.set(true);
    this._addListeners();
    const panelEl = this.panelRef?.nativeElement;
    if (panelEl) {
      ContextMenu._openMenus.push({ panel: panelEl, trigger: triggerEl });
    }
    this.opened.emit();
    this._cdr.markForCheck();
  }

  close() {
    if (!this.isOpen()) return;
    this.isOpen.set(false);
    this._demoteFromTopLayer();
    const panelEl = this.panelRef?.nativeElement;
    if (panelEl) {
      const idx = ContextMenu._openMenus.findIndex((m) => m.panel === panelEl);
      if (idx !== -1) ContextMenu._openMenus.splice(idx, 1);
    }
    this._triggerEl = null;
    this._removeListeners();
    this.closed.emit();
    this._cdr.markForCheck();
  }

  private _movePanelToBody() {
    const panelEl = this.panelRef?.nativeElement;
    if (!panelEl) return;
    if (!this._originalParent) {
      this._originalParent = panelEl.parentElement;
    }
    document.body.appendChild(panelEl);
  }

  private _returnPanelToOriginalParent() {
    const panelEl = this.panelRef?.nativeElement;
    if (!panelEl || !this._originalParent) return;
    if (panelEl.parentElement === document.body) {
      this._originalParent.appendChild(panelEl);
    }
  }

  private _computePosition(): Record<string, string> {
    if (!this._triggerEl) return {};
    const triggerRect = this._triggerEl.getBoundingClientRect();

    // Degenerate rect means the trigger is detached or `display: none` — e.g.
    // a hover/focus-revealed button whose parent lost :hover while the menu
    // is open. Recomputing here would place the panel at (0, 0). Keep the
    // last-known position instead so the panel stays anchored where it was.
    if (triggerRect.width === 0 && triggerRect.height === 0) {
      return this.panelStyle;
    }

    const gap = this.overlapTrigger() ? 0 : 4;

    let yDir = this._yOverride ?? this.yPosition();
    let xDir = this._xOverride ?? this.xPosition();

    // Viewport flip: measure the panel (use a reasonable fallback). Measure it
    // at its natural size: where it sat before can squeeze it (a panel
    // anchored by its right edge near the left screen edge shrinks to fit).
    // The previous inline offsets are restored so the bindings stay in sync.
    const panelEl = this.panelRef?.nativeElement;
    let panelWidth = 200;
    let panelHeight = 200;
    if (panelEl) {
      const { left, right } = panelEl.style;
      panelEl.style.left = '0px';
      panelEl.style.right = 'auto';
      panelWidth = panelEl.offsetWidth || panelWidth;
      panelHeight = panelEl.offsetHeight || panelHeight;
      panelEl.style.left = left;
      panelEl.style.right = right;
    }

    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    // Fixed positioning is relative to the viewport without its scrollbar,
    // which `window.innerWidth` would include.
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const spaceRight = viewportWidth - triggerRect.right;
    const spaceLeft = triggerRect.left;

    if (yDir === 'below' && spaceBelow < panelHeight && spaceAbove > spaceBelow) {
      yDir = 'above';
    } else if (yDir === 'above' && spaceAbove < panelHeight && spaceBelow > spaceAbove) {
      yDir = 'below';
    }

    if (xDir === 'after' && spaceRight < panelWidth && spaceLeft > spaceRight) {
      xDir = 'before';
    } else if (xDir === 'before' && spaceLeft < panelWidth && spaceRight > spaceLeft) {
      xDir = 'after';
    }

    const style: Record<string, string> = {};

    // Vertical positioning
    if (yDir === 'below') {
      style['top'] = `${triggerRect.bottom + gap}px`;
      style['bottom'] = 'auto';
    } else {
      style['bottom'] = `${window.innerHeight - triggerRect.top + gap}px`;
      style['top'] = 'auto';
    }

    // Horizontal positioning. A panel that fits on neither side of the trigger
    // (small screens) is shifted to stay `margin` inside the viewport; it is
    // never wider than the viewport minus both margins (see `max-width` in the
    // stylesheet), so on a phone it spans the screen width.
    const margin = viewportMargin();
    const naturalLeft = xDir === 'after' ? triggerRect.left : triggerRect.right - panelWidth;
    const maxLeft = viewportWidth - margin - panelWidth;
    if (naturalLeft < margin || naturalLeft > maxLeft) {
      style['left'] = `${Math.max(margin, Math.min(naturalLeft, maxLeft))}px`;
      style['right'] = 'auto';
    } else if (xDir === 'after') {
      style['left'] = `${triggerRect.left}px`;
      style['right'] = 'auto';
    } else {
      style['right'] = `${viewportWidth - triggerRect.right}px`;
      style['left'] = 'auto';
    }

    if (this.matchTriggerWidth()) {
      style['width'] = `${triggerRect.width}px`;
    }

    return style;
  }

  // Use mousedown (not click) so a drag that starts inside the panel and ends
  // outside — e.g. lifting the mouse off the panel mid text-selection — does
  // not dismiss the menu. The decision to close is made at press time.
  private _onDocumentMousedown = (event: MouseEvent) => {
    if (!this.isOpen()) return;
    const target = event.target as Node;
    const panelEl = this.panelRef?.nativeElement;
    if (!panelEl) return;
    if (panelEl.contains(target)) return;
    if (this._triggerEl?.contains(target)) return;
    // A panel whose trigger lives inside this panel is a child popover — a
    // press inside that child should not dismiss us.
    for (const other of ContextMenu._openMenus) {
      if (other.panel === panelEl) continue;
      if (other.trigger && panelEl.contains(other.trigger) && other.panel.contains(target)) {
        return;
      }
    }
    // This panel is portaled into the browser top layer, above any CDK overlay
    // (e.g. a MatDialog). When the dismiss-press lands on that overlay's
    // backdrop, closing here isn't enough: the trailing `click` would still
    // reach the backdrop and dismiss the overlay (the dialog) too. Swallow that
    // one click so only this popover closes — a second click still hits the
    // backdrop. Popovers opened outside any overlay have no backdrop to match,
    // so this is a no-op for them.
    if (target instanceof Element && target.closest('.cdk-overlay-backdrop')) {
      this._swallowNextClick();
    }
    this.close();
  };

  // Eat the `click` that completes the current outside press, in the capture
  // phase, so it never reaches the element under the cursor. Self-removes once
  // it fires; also dropped on the next `mousedown`, so a press that yields no
  // click (e.g. a drag released elsewhere) can't leave a stale guard that would
  // later swallow an unrelated click.
  private _swallowNextClick() {
    const cleanup = () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('mousedown', cleanup, true);
    };
    const onClick = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      cleanup();
    };
    document.addEventListener('click', onClick, true);
    document.addEventListener('mousedown', cleanup, true);
  }

  private _onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.close();
    }
  };

  private _onScrollOrResize = (event?: Event) => {
    // Scrolls originating inside the panel itself (e.g. an emoji picker
    // category scrolling its own list) cannot move the trigger, so they
    // shouldn't trigger a reposition. Filtering them out also avoids racing
    // with a trigger whose visibility is bound to :hover/:focus-within on a
    // bubble the cursor has since left.
    if (event && event.type === 'scroll') {
      const target = event.target as Node | null;
      const panelEl = this.panelRef?.nativeElement;
      if (panelEl && target && target.nodeType === Node.ELEMENT_NODE && panelEl.contains(target)) {
        return;
      }
    }
    if (this._rafId !== null) return;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      if (!this.isOpen()) return;
      this.panelStyle = this._computePosition();
      this._cdr.markForCheck();
    });
  };

  private _addListeners() {
    document.addEventListener('mousedown', this._onDocumentMousedown, true);
    document.addEventListener('keydown', this._onKeydown);
    window.addEventListener('scroll', this._onScrollOrResize, true);
    window.addEventListener('resize', this._onScrollOrResize);
  }

  private _removeListeners() {
    document.removeEventListener('mousedown', this._onDocumentMousedown, true);
    document.removeEventListener('keydown', this._onKeydown);
    window.removeEventListener('scroll', this._onScrollOrResize, true);
    window.removeEventListener('resize', this._onScrollOrResize);
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }
}

/** Gap kept between a panel and the viewport edges: 0.5rem (see the stylesheet). */
function viewportMargin(): number {
  const rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return (Number.isFinite(rem) ? rem : 16) * 0.5;
}
